/**
 * Pure logic behind two CRF Builder fixes. React-free so it can be tested from
 * the `functions` package's vitest (`web` has no runner — see
 * features/admin/adminAccess.ts for the same arrangement).
 */

export interface CrossDupOccurrence {
  formId: string;
  formName: string;
}

/** The slice of a duplicate warning this module needs. */
export interface CrossDupWarning {
  field?: string;
  duplicateOccurrences?: CrossDupOccurrence[];
}

export interface CrossDupGroupVariable {
  varName: string;
  occurrences: CrossDupOccurrence[];
}

export interface CrossDupGroup {
  /** Stable across encounter order — the sorted form set. */
  key: string;
  /** Display order: as the first member of the group listed them. */
  formNames: string[];
  variables: CrossDupGroupVariable[];
  /** Convenience for the bulk-confirm action. */
  variableNames: string[];
}

/**
 * Collapse cross-form duplicate warnings into one entry per distinct SET of
 * forms.
 *
 * Why grouping rather than a blanket "confirm all": on a real study, 31
 * warnings included four variables (VSDAT / VSTIM / VSPERF / VSPOS) that all
 * span exactly "Vital Signs + Height Measurement". Those are one decision, not
 * four — the question a coordinator answers is "is it intentional that these
 * two forms share fields", and the answer covers every variable in the group.
 * Grouping turns a list of 31 individual judgements into a handful.
 *
 * Largest group first, so the biggest reduction in clicks is at the top.
 * Encounter order breaks ties, so the list is stable between renders.
 */
export function groupCrossFormDuplicates(dups: CrossDupWarning[]): CrossDupGroup[] {
  const byKey = new Map<string, CrossDupGroup & { firstSeen: number }>();

  dups.forEach((dup, index) => {
    const varName = (dup.field ?? '').trim();
    // A warning with no variable name cannot be confirmed or renamed, and
    // lumping several together under an empty key would make one meaningless
    // group. Drop it rather than display it.
    if (!varName) return;

    const occurrences = dup.duplicateOccurrences ?? [];
    const formNames = occurrences.map((o) => o.formName);
    // Order-insensitive: the same two forms listed either way are one group.
    const key = [...formNames].sort().join(' ');

    const existing = byKey.get(key);
    if (existing) {
      existing.variables.push({ varName, occurrences });
      existing.variableNames.push(varName);
      return;
    }
    byKey.set(key, {
      key,
      formNames,
      variables: [{ varName, occurrences }],
      variableNames: [varName],
      firstSeen: index,
    });
  });

  return [...byKey.values()]
    .sort((a, b) => b.variables.length - a.variables.length || a.firstSeen - b.firstSeen)
    .map(({ firstSeen: _firstSeen, ...group }) => group);
}

/**
 * How many approved forms actually carry a rendering.
 *
 * `total` counts only approved/final forms because a draft has nothing to
 * render, so "3 of 3 rendered" stays truthful on a part-finished study. Both
 * predicates are injected so this stays free of the CRF type and of
 * `hasUsableRendering`'s dependencies; the caller passes the same predicates
 * the server precondition uses.
 */
export function summariseRenderings<T>(
  forms: T[],
  isApproved: (form: T) => boolean,
  isRendered: (form: T) => boolean,
): { rendered: number; total: number } {
  const approved = forms.filter(isApproved);
  return {
    rendered: approved.filter(isRendered).length,
    total: approved.length,
  };
}
